import { z } from "../zod.js";
import { Locale } from "../locales.js";

// Module 02 — Onboarding domain events (spec 02 §6). The typed catalog of the
// 11 PRODUCER events this module emits (past-tense two-segment names,
// `onboarding.subject_pastverb`, D-015) + their payload shapes, plus the set of
// events this module CONSUMES. Emitters write to the transactional outbox in
// the same txn as the mutation (the events rule); the envelope
// (event_id, tenant_id, occurred_at, actor) is added centrally by OutboxService.
//
// This is the contracts-layer home foreseen by D-015 / TESTING §5.2 (domain-
// event payloads as zod schemas). It is intentionally NOT registered into the
// OpenAPI document (events are not HTTP routes) — so it adds zero paths/schemas
// to openapi/v1.json. Phase 2's onboarding event emitter imports these payload
// schemas so every emit site is typed against the same contract the §6
// consumers parse.

/** Actor kind on the outbox envelope (spec 02 §6). */
export const OnboardingEventActorKind = z.enum(["user", "agent", "system"]);

// --- producer payloads (spec 02 §6 table, in order) ---

export const OnboardingSessionStartedPayload = z.object({
  session_id: z.guid(),
  started_by_user_id: z.guid(),
});

export const OnboardingPackInstalledPayload = z.object({
  install_id: z.guid(),
  pack_key: z.string(),
  pack_version: z.string(),
  // `seeded_counts` is the pack's per-kind CARDINALITY (how many objects the pack
  // declares for each kind), NOT a materialized-row count: a staged/snapshot kind
  // reports its pack size while materializing zero owning-module rows (spec 02
  // §2.3). Consumers that need "what actually exists" cross-reference the
  // seeded/skipped breakdown below (or the per-kind disposition on
  // GET /v1/verticals/installs/current).
  seeded_counts: z.record(z.string(), z.number().int()),
  // The disposition breakdown (spec 02 §6): which object_kinds materialized via an
  // owning-module service (seeded_kinds) vs. were honestly skipped because their
  // owning module is not wired into this deployment yet (skipped_kinds, each
  // naming the module). Module 14 loads pack context from this; Module 13/15
  // report install completeness. Snapshot-only kinds (§2.4) appear in neither.
  seeded_kinds: z.array(z.string()),
  skipped_kinds: z.array(z.object({ object_kind: z.string(), owner_module: z.string() })),
  // The RE-install summary (D-062, spec 02 §3.2/§11). A first install reports
  // both as empty; a re-install (the §3.2 force/pack-switch path) reports what
  // it brought forward onto the pack's current definition and — more
  // importantly — what it deliberately left alone and why. `reason` is:
  //   · `tenant_owned` — module 01 demoted the row's `source` when a human
  //     edited a pack-owned column;
  //   · `customized` — a `pack_seed_links.customized` flag (§6.1);
  //   · `would_invalidate_products` — the corrected pack object is structurally
  //     incompatible with data the tenant already holds. Today that is an
  //     attribute schema whose new `json_schema` would fail validation against
  //     products already bound to it; `affected_count` carries how many. The
  //     schema is RETAINED because converging is the unrecoverable direction —
  //     it would leave those products 422-ing on their next unrelated edit — and
  //     the case is handed to the D-025 migration assistant.
  // This is §11's "customized objects are skipped and flagged" made concrete and
  // per-object rather than per-kind.
  converged_objects: z.array(z.object({ object_kind: z.string(), pack_ref: z.string() })),
  retained_objects: z.array(
    z.object({
      object_kind: z.string(),
      pack_ref: z.string(),
      reason: z.enum(["tenant_owned", "customized", "would_invalidate_products"]),
      /** The tenant's own rows behind this retention (products, for
       * `would_invalidate_products`). Absent when the reason carries no count. */
      affected_count: z.number().int().nonnegative().optional(),
    }),
  ),
});

export const OnboardingPackInstallFailedPayload = z.object({
  install_id: z.guid(),
  pack_key: z.string(),
  error: z.object({
    code: z.string(),
    message_key: z.string(),
    detail: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const OnboardingStepCompletedPayload = z.object({
  session_id: z.guid(),
  step_no: z.number().int().min(1).max(4),
  completed_by_user_id: z.guid(),
});

export const OnboardingImportCommittedPayload = z.object({
  import_job_id: z.guid(),
  kind: z.string(),
  facility_id: z.guid().nullable(),
  committed_rows: z.number().int().min(0),
  skipped_rows: z.number().int().min(0),
  error_rows: z.number().int().min(0),
  /** Value-posting kinds (opening_stock): Σ committed rows' value in integer
   * minor units (tenant currency) — Module 11's opening-JE input (D-050). */
  value_total_minor: z.number().int().min(0).optional(),
});

export const OnboardingImportFailedPayload = z.object({
  import_job_id: z.guid(),
  kind: z.string(),
  error: z.object({
    code: z.string(),
    message_key: z.string(),
    detail: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const OnboardingQrKitGeneratedPayload = z.object({
  batch_id: z.guid(),
  kind: z.string(),
  item_count: z.number().int().min(0),
  facility_id: z.guid().nullable(),
});

export const OnboardingChecklistEvaluatedPayload = z.object({
  session_id: z.guid(),
  score: z.number().int().min(0).max(100),
  blocking_unmet_keys: z.array(z.string()),
});

export const OnboardingChecklistItemWaivedPayload = z.object({
  session_id: z.guid(),
  item_key: z.string(),
  waived_by_user_id: z.guid(),
  blocking: z.boolean(),
});

export const OnboardingWentLivePayload = z.object({
  session_id: z.guid(),
  went_live_by_user_id: z.guid(),
  completeness_score: z.number().int().min(0).max(100),
  // NULLABLE (Phase 2e): `vertical.pack_installed` is blocking but WAIVABLE by a
  // group_admin (spec 02 §3.6), so a tenant can legitimately go live with no
  // installed pack — the event must be able to say so rather than invent a key.
  pack_key: z.string().nullable(),
  facilities_count: z.number().int().min(0),
  products_count: z.number().int().min(0),
});

export const OnboardingTerminologyUpdatedPayload = z.object({
  term_key: z.string(),
  locale: Locale,
  // `base` added in Phase 2a: a "Reset to pack" (DELETE) that reverts a term to
  // the base string emits source='base' so the frontend cache-bust (Module 12
  // SSE) + agent glossary (Module 14) learn the new EFFECTIVE origin, not just
  // pack/tenant edits (spec 02 §2.5, §6).
  source: z.enum(["base", "pack", "tenant"]),
});

/**
 * The 11 producer events (spec 02 §6) keyed by name → payload schema, with the
 * declared consumer module ids. The keys ARE the wire event names; the D-015
 * reconcile pairs each producer against its consumers' subscriptions.
 */
export const onboardingProducerEvents = {
  "onboarding.session_started": {
    payload: OnboardingSessionStartedPayload,
    consumers: ["13", "15"],
  },
  "onboarding.pack_installed": {
    payload: OnboardingPackInstalledPayload,
    consumers: ["13", "14", "15"],
  },
  "onboarding.pack_install_failed": {
    payload: OnboardingPackInstallFailedPayload,
    consumers: ["15", "12"],
  },
  "onboarding.step_completed": {
    payload: OnboardingStepCompletedPayload,
    consumers: ["13", "02"],
  },
  "onboarding.import_committed": {
    payload: OnboardingImportCommittedPayload,
    consumers: ["02", "13", "03", "11"],
  },
  "onboarding.import_failed": {
    payload: OnboardingImportFailedPayload,
    consumers: ["12"],
  },
  "onboarding.qr_kit_generated": {
    payload: OnboardingQrKitGeneratedPayload,
    consumers: ["12"],
  },
  "onboarding.checklist_evaluated": {
    payload: OnboardingChecklistEvaluatedPayload,
    consumers: ["13", "15"],
  },
  "onboarding.checklist_item_waived": {
    payload: OnboardingChecklistItemWaivedPayload,
    consumers: ["15"],
  },
  "onboarding.went_live": {
    payload: OnboardingWentLivePayload,
    // Module 03 is not a consumer: its worker runs whatever the live status (D-349 ruling 14).
    consumers: ["16", "12", "13", "15"],
  },
  "onboarding.terminology_updated": {
    payload: OnboardingTerminologyUpdatedPayload,
    consumers: ["12", "14"],
  },
} as const;

export type OnboardingEventName = keyof typeof onboardingProducerEvents;

export const onboardingProducerEventNames = Object.keys(
  onboardingProducerEvents,
) as OnboardingEventName[];

/**
 * Events module 02 CONSUMES (spec 02 §6 "Consumed by this module").
 *
 * Since D-058 these are no longer declarations awaiting a transport: the
 * outbox relay exists and `PackCustomizationSubscriber` is registered against
 * the first three. `outbox-subscriber-parity.test.ts` asserts this list
 * against the live registry BOTH WAYS, so a name here with no subscriber (and
 * no recorded reason) fails, and so does a subscriber for a name absent here.
 *
 *  - identity.role_updated ......... WIRED and flipping (a pack-seeded role's
 *    permissions edited → `pack_seed_links.customized`).
 *  - onboarding.terminology_updated  WIRED and flipping on source='tenant'
 *    (self-consume, the §6 "checklist evaluator" precedent).
 *  - catalogue.product_updated ..... WIRED; matches no seed link today because
 *    no pack seeds a product (see the subscriber's header).
 *  - catalogue.uom_updated ......... WIRED and flipping (D-062). Module 01's
 *    `updateUom` emits it; a pack-seeded unit's seed link `entity_id` IS
 *    `uoms.id`, so a tenant renaming or re-precisioning a pack unit marks it
 *    customized. This is the producer §6.1 had recorded as missing.
 *  - catalogue.attribute_schema_updated  WIRED and flipping (D-062), same
 *    shape via `updateAttributeSchema`.
 *  - production.operation_completed  UNWIRED — Module 04 does not exist, so
 *    the `channels.floor_pwa_tested` checklist item has no producer.
 *
 * There is deliberately NO `catalogue.template_updated`: module 01 exposes no
 * template PATCH (create + archive only), so no tenant edit path exists to
 * report. A tenant-authored template is `source='custom'` from birth and the
 * installer's SQL fence rejects it without needing a flag.
 */
export const onboardingConsumedEventNames = [
  "identity.role_updated",
  "onboarding.terminology_updated",
  "catalogue.product_updated",
  "catalogue.uom_updated",
  "catalogue.attribute_schema_updated",
  "production.operation_completed",
] as const;
